// packages/studio/web/src/editor/StudioComposition.tsx
//
// Studio convergence: mount the SAME composable viewer surface the standalone
// viewer uses, over Studio's changeset store. The store is the bridge —
// `useStudioWorkspace` seeds base=committed-trunk / working=draft and autosaves
// drafts; we hand that store to `ViewerProviders` so the shared workbench
// primitives drive Studio's editor.
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { ResizablePanel, WIDTH_TIERS, useToast } from "@archlang/ui";
import { Image as ImageIcon, Code, Copy, Puzzle } from "lucide-react";
import {
  publishDynamicMenuItems,
  type MenuItemContribution,
} from "@archlang/app-shell/board";
import {
  ViewerProviders,
  useWorkbenchController,
  DetailsPane,
  SearchPalette,
  TypesPane,
  DocumentsPane,
  ProcessPane,
  useCompare,
  useTabs,
  useWorkspace,
  useActiveFile,
  useActiveTabId,
  READONLY,
  useDiffChangeSet,
  clearDiffState,
  usePanelKindResolver,
  useNavSectionContributions,
  useWorkbenchHost,
  PluginBoundary,
  CommandPaletteHost,
  type FileSource,
  type WorkspaceStore,
} from "@archlang/app-shell/workbench";
import {
  Editor,
  TabStrip,
  WorkspaceNav,
  useExportShareActions,
  EditorBoardArea,
  EditorLayoutMenu,
  useStoreNavSource,
  useWorkspaceModel,
  buildModeTabs,
  navViewForMode,
  AdvisoryPane,
  BoardSurface,
  ModulePage,
  useModelIndexes,
  useCustomTabEntityReconcile,
  useNativeContextMenuSuppression,
  useOptionalEditMode,
  PanelToggle,
  isBoardVisible,
  useActiveTabKind,
  useProcessImageExport,
  type CreateWorker,
} from "@archlang/app-shell/editor";
import { isActionable } from "@archlang/app-shell/board";
import { Spinner } from "../ui/Spinner.js";

const TRUNK_SOURCE: FileSource = {
  label: "trunk",
  capabilities: READONLY,
  load: () => null,
};

export interface StudioCompositionProps {
  readonly store: WorkspaceStore;
  readonly ready: boolean;
  readonly changesetLabel: string;
  readonly createLspWorker: CreateWorker;
  readonly lspExtras?: ReadonlyMap<string, string>;
  readonly headerActions?: ReactNode;
  readonly persistNamespace?: string;
}

export function StudioComposition(props: StudioCompositionProps): ReactNode {
  if (!props.ready) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Spinner />
      </div>
    );
  }
  return (
    <ViewerProviders source={TRUNK_SOURCE} store={props.store}>
      <StudioCompositionInner
        label={props.changesetLabel}
        createLspWorker={props.createLspWorker}
        lspExtras={props.lspExtras}
        headerActions={props.headerActions}
        persistNamespace={props.persistNamespace}
      />
      {/* The root ⌘K command palette — merges the plugin `commands` seam
          (historyPlugin's New proposal / Propose changes, otlpPlugin's) with
          live command sources. Adopted into Studio's bespoke composition the
          same way ViewerComposition mounts it. */}
      <CommandPaletteHost />
    </ViewerProviders>
  );
}

function StudioCompositionInner({
  label,
  createLspWorker,
  lspExtras,
  headerActions,
  persistNamespace,
}: {
  label: string;
  createLspWorker: CreateWorker;
  lspExtras?: ReadonlyMap<string, string>;
  headerActions?: ReactNode;
  persistNamespace?: string;
}): ReactNode {
  const c = useWorkbenchController({ exportLabel: label, persistNamespace });
  // Platform takeover: no native browser context menu anywhere in the shell
  // (same as the viewer) — board/nav right-clicks open OUR menus.
  useNativeContextMenuSuppression();
  const navSource = useStoreNavSource();
  const store = useWorkspace();
  const tabs = useTabs(store);
  const { documents, backlinks, model } = useWorkspaceModel();
  const compare = useCompare();
  const diffChangeSet = useDiffChangeSet();

  // Clear diff mode store when we leave diff mode (or on unmount).
  useEffect(() => {
    if (!compare.inDiffMode) clearDiffState();
  }, [compare.inDiffMode]);

  const activeTabKind = useActiveTabKind(store);
  const activeTabId = useActiveTabId(store);

  // Parallel the viewer's hasEditorSurface: only show Monaco when a file or
  // doc tab is active (not for board/types/process tabs).
  const isDocTab = activeTabKind === "doc";
  const activeTab = tabs.find((t) => t.id === activeTabId);

  // Plugin surfaces adopted into Studio's bespoke composition (the viewer path
  // wires these in ViewerComposition/WorkspacePanel; Studio mounts them here):
  //  - custom panelKind tabs → rendered full-bleed in the centre;
  //  - navSections → extra rail tabs + panels on the WorkspaceNav.
  // TODO(workbench-convergence): the resolve-custom-tab + visibleSections/
  // sectionPanels wiring below duplicates ViewerComposition + WorkspacePanel
  // (down to the fallback Puzzle icon). Lift into shared app-shell hooks
  // (useCustomTabRenderer / useNavSectionTabs) once Studio migrates onto the
  // shared composition, so both consume one source instead of drifting.
  const hostCtx = useWorkbenchHost();
  const resolvePanelKind = usePanelKindResolver();
  const customTab = activeTab?.kind === "custom" ? activeTab : null;
  const customPanelKind = customTab ? resolvePanelKind(customTab.state.panelKind) : undefined;

  const navSections = useNavSectionContributions();
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const visibleSections = useMemo(
    () => navSections.filter((o) => isActionable(o.contribution, hostCtx)),
    [navSections, hostCtx],
  );
  const activeSectionId = visibleSections.some((o) => o.contribution.id === activeSection)
    ? activeSection
    : null;
  const sectionTabItems = useMemo(
    () =>
      visibleSections.map((o) => ({
        id: o.contribution.id,
        label: o.contribution.title,
        icon: o.contribution.icon ?? <Puzzle className="h-5 w-5" strokeWidth={1.8} aria-hidden />,
      })),
    [visibleSections],
  );
  const sectionPanels = useMemo(() => {
    const m = new Map<string, ReactNode>();
    for (const o of visibleSections) {
      const Panel = o.contribution.Panel;
      m.set(
        o.contribution.id,
        <PluginBoundary pluginId={o.pluginId} key={o.contribution.id}>
          <Panel ctx={hostCtx} />
        </PluginBoundary>,
      );
    }
    return m;
  }, [visibleSections, hostCtx]);

  const activeDocPath = activeTab?.kind === "doc" ? activeTab.state.path : null;
  const activeDocSlug = activeTab?.kind === "doc" ? activeTab.state.slug : undefined;
  // Viewer parity: the editor also pairs with the process view (read the code
  // while tracing a process) — as long as a file is active.
  const activeFilePath = useActiveFile(store);
  const hasEditorSurface =
    activeTabKind === "file" ||
    isDocTab ||
    (activeTabKind === "process" && activeFilePath !== null);

  // The markdown document surface is its own `doc` tab.
  const docPath = activeDocPath;
  const docSlug = activeDocSlug;
  const isDoc = isDocTab && !!docPath;

  // Merge boardHoverStore and navReveal from controller into the nav source so
  // board-item hover ↔ nav-row hover and board double-click → nav scroll work.
  const mergedNavSource = useMemo(
    () => ({
      ...navSource,
      hoverStore: c.boardHoverStore ?? navSource.hoverStore,
      navReveal: () => c.navReveal,
      // Studio's "Revert to main" lives in the system header — suppress the
      // nav-header duplicate so it doesn't appear twice.
      revertAll: undefined,
      // In diff mode: surface the changeset in the left WorkspaceNav so the
      // user sees changed entities without needing DiffBoard's internal DiffNav.
      ...(diffChangeSet
        ? { diff: () => diffChangeSet, diffSelection: () => null }
        : {}),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [navSource, c.boardHoverStore, c.navReveal, diffChangeSet],
  );

  // Studio always has an LSP worker — editing is possible on every surface; the
  // per-view edit STANCE (view ↔ edit) is the shared EditToggle in the board HUD.
  const canEdit = true;
  const editMode = useOptionalEditMode();
  const editableNow = canEdit && editMode;

  // Reframe camera when the editor/board split orientation changes — without
  // this the old camera leaves nodes off-screen in the reshaped pane.
  const prevLayoutRef = useRef(c.editorLayout);
  useEffect(() => {
    if (prevLayoutRef.current === c.editorLayout) return;
    prevLayoutRef.current = c.editorLayout;
    if (!hasEditorSurface) return;
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => c.boardRef.current?.reframe());
    });
    return () => {
      cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
    };
  }, [c.editorLayout, hasEditorSurface, c.boardRef]);

  // Process-view image export (PNG/SVG of the active flow/BPMN/sequence diagram).
  const processExport = useProcessImageExport(model ?? null, c.nodeColoring);

  // A custom tab whose entity left the model gets offered for closing —
  // shared workbench hook (same as the viewer).
  useCustomTabEntityReconcile(model ?? null);

  // Friendly labels for rooted board / process / module tabs: resolve the
  // entity id to its model name (else the strip falls back to the raw id).
  const modelIndexes = useModelIndexes(model ?? null);
  const entityLabel = useCallback(
    (id: string): string | undefined =>
      modelIndexes?.moduleById.get(id)?.name ??
      modelIndexes?.processById.get(id)?.name ??
      undefined,
    [modelIndexes],
  );

  // Export/share menu items — the migrated `exportSectionStore` payload. These
  // are board-coupled (they close over the live board ref, active tab kind, and
  // the process-image export), so they publish through the registry bridge's
  // DYNAMIC menu-items channel rather than as static plugin data. The BrandMenu
  // merges them with any static plugin `menuItems` under the "Export" group.
  const { success: exportSuccess, error: exportError } = useToast();
  const exportActions = useExportShareActions(store, {
    capture: c.boardRef,
    label,
    qualifiedNameById: undefined,
  });
  const runExportCapture = useCallback(
    async (fn: () => Promise<void>) => {
      try { await fn(); }
      catch { exportError("Export failed."); }
    },
    [exportError],
  );
  const exportMenuItems = useMemo<MenuItemContribution[]>(() => {
    const isProcess = activeTabKind === "process";
    const noun = isProcess ? " process" : "";
    const overridePng = isProcess ? processExport?.exportPng : undefined;
    const overrideSvg = isProcess ? processExport?.exportSvg : undefined;
    const busy = exportActions.exportingPng;
    const ICON = { width: 16, height: 16, strokeWidth: 1.6, "aria-hidden": true } as const;
    const items: MenuItemContribution[] = [
      {
        id: "export-png",
        group: "Export",
        icon: <ImageIcon {...ICON} />,
        label: `Export${noun} PNG`,
        disabled: busy,
        run: () => void runExportCapture(overridePng ?? exportActions.handleExportPng),
      },
      {
        id: "export-svg",
        group: "Export",
        icon: <Code {...ICON} />,
        label: `Export${noun} SVG`,
        disabled: busy,
        run: () => overrideSvg
          ? overrideSvg()
          : void runExportCapture(exportActions.handleExportSvg),
      },
    ];
    if (!isProcess) {
      items.push({
        id: "copy-img",
        group: "Export",
        icon: <Copy {...ICON} />,
        label: "Copy image",
        run: () => {
          exportActions.handleCopyImage()
            .then(() => exportSuccess("Image copied to clipboard."))
            .catch(() => exportError("Couldn't copy the image."));
        },
      });
    }
    return items;
  }, [activeTabKind, processExport, exportActions, runExportCapture, exportSuccess, exportError]);

  useEffect(() => {
    publishDynamicMenuItems(exportMenuItems);
    return () => publishDynamicMenuItems([]);
  }, [exportMenuItems]);

  // The whole board center — HUD chips (edit toggle, view builder/persist/
  // health, knobs, aspects, plugin header items, plane switcher, layout reset),
  // the on-board step scrubber, the representation switch (draft-view aware, so
  // the WYSIWYG builder previews live), the fully-wired BoardPane (rooted tabs,
  // per-tab viewport persistence, structural editing) and the color legend —
  // is the SHARED BoardSurface primitive: the exact surface the viewer mounts.
  const boardCenter = (
    <div className="relative min-h-0 min-w-0 flex-1">
      <BoardSurface c={c} canEdit={canEdit} operator={hasEditorSurface} />
    </div>
  );

  const center =
    c.mode === "types" ? (
      <TypesPane
        onJumpToInstance={c.handleJumpToInstance}
        onArchlangNav={c.handleArchlangNav}
      />
    ) : c.mode === "process" ? (
      // ProcessPane carries its own header scrubber (ProcessMode) — same as the
      // viewer; the floating board scrubber belongs to BoardSurface only.
      <div className="relative min-h-0 min-w-0 flex-1">
        <ProcessPane onSelectModule={c.handleNavigateToModule} />
      </div>
    ) : activeTab?.kind === "module" ? (
      // A module page tab (opened from the index representation / module links)
      // — same center the viewer mounts for the `module` tab kind.
      <ModulePage
        moduleId={activeTab.state.moduleId}
        moduleName={activeTab.state.moduleName}
      />
    ) : (
      boardCenter
    );

  // The editor body: Monaco for file tabs, DocumentsPane for doc tabs.
  const editorBody = isDoc ? (
    docPath ? (
      <DocumentsPane
        key={docPath}
        documents={documents}
        backlinks={backlinks}
        selectedDocName={documents.find((d) => d.path === docPath)?.name ?? null}
        scrollToSlug={docSlug}
        onArchlangNav={c.handleArchlangNav}
      />
    ) : null
  ) : (
    <div className="flex h-full min-h-0 flex-col bg-slate-900">
      <div className="relative min-h-0 flex-1">
        <Editor createLspWorker={createLspWorker} lspExtras={lspExtras} />
      </div>
    </div>
  );

  // Whether the details panel is relevant (board is on screen). Same mapping
  // as the viewer — a module page replaces the board, so no inspector beside it.
  const view: "board" | "types" | "process" | "docs" | "module" =
    activeTabKind === "types"
      ? "types"
      : activeTabKind === "doc"
        ? "docs"
        : activeTabKind === "process"
          ? "process"
          : activeTabKind === "module"
            ? "module"
            : "board";

  const showDetails =
    !compare.inDiffMode &&
    !customTab &&
    isBoardVisible({
      isBoardView: view === "board" || view === "docs",
      showEditor: hasEditorSurface,
      editorLayout: c.editorLayout,
    });

  // Nav rail tabs = built-in mode tabs + plugin nav-section tabs (with their
  // panels), merged the same way WorkspacePanel does for the viewer.
  const modeTabs = buildModeTabs({
    active: c.navTab,
    onSelect: (id) => {
      setActiveSection(null);
      c.setNavTab(id);
      // Same grammar as the viewer (ViewerComposition's onTabChange): only the
      // Board tab flips the center perspective. Types / Process / Views / Docs
      // just swap the list shown in the nav rail — the center changes when you
      // OPEN an item (double-click), not when you browse a category.
      if (id === "board") c.setMode(id);
    },
  });
  const navTabsProp = {
    items: [...modeTabs.items, ...sectionTabItems],
    active: activeSectionId ?? modeTabs.active,
    onSelect: (id: string) => {
      if (sectionPanels.has(id)) {
        setActiveSection(id);
      } else {
        setActiveSection(null);
        modeTabs.onSelect(id);
      }
    },
  };

  const widthKey = (suffix: string) =>
    `archlang.studio.${suffix}.v1${persistNamespace ? `.${persistNamespace}` : ""}`;

  return (
    <div className="relative flex h-full w-full bg-slate-950 text-slate-100">
      {/* Left: resizable navigator */}
      <ResizablePanel
        side="left"
        ariaLabel="navigator"
        widthKey={widthKey("nav.width")}
        collapsed={c.navCollapsed}
        onCollapsedChange={c.setNavCollapsed}
        showHandle={false}
        defaultWidth={240}
        {...WIDTH_TIERS.rail}
        className="flex flex-col border-r border-slate-800"
      >
        {headerActions ? (
          <div className="flex items-center gap-1 border-b border-slate-800 px-2 py-1.5">
            {headerActions}
          </div>
        ) : null}
        <WorkspaceNav
          source={mergedNavSource}
          view={navViewForMode(c.navTab)}
          tabs={navTabsProp}
          sectionPanels={sectionPanels}
          headerExtra={undefined}
          persistKey={persistNamespace ?? "studio"}
          hotkey
          searchInputRef={c.searchInputRef}
          className="flex-1"
        />
      </ResizablePanel>

      {/* Right column: tab strip on top, then content row */}
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        {/* Tab strip with panel-collapse toggles + editor layout menu. A custom
            (plugin) review tab drives the compare store internally, so keep the
            strip visible for it even though inDiffMode is set. */}
        {(!compare.inDiffMode || customTab) && (
          <TabStrip
            entityLabel={entityLabel}
            leading={
              <PanelToggle
                side="left"
                label="navigator"
                variant="strip"
                collapsed={c.navCollapsed}
                onToggle={() => c.setNavCollapsed((v) => !v)}
              />
            }
            trailing={
              hasEditorSurface || showDetails ? (
                <div className="flex items-center gap-1">
                  {hasEditorSurface && (
                    <EditorLayoutMenu
                      value={c.editorLayout}
                      onChange={c.setEditorLayout}
                      options={c.editorLayoutOptions}
                    />
                  )}
                  {showDetails && (
                    <PanelToggle
                      side="right"
                      label="details"
                      variant="strip"
                      collapsed={c.sidebarCollapsed}
                      onToggle={() => c.setSidebarCollapsed((v) => !v)}
                    />
                  )}
                </div>
              ) : undefined
            }
          />
        )}

        {/* Content row */}
        <div className="flex min-h-0 flex-1 overflow-hidden">
          {customTab && customPanelKind ? (
            // A plugin custom tab (e.g. the history/review panel) renders
            // full-bleed; it owns its own internal layout + diff/compare state.
            <PluginBoundary pluginId={customTab.state.pluginId}>
              <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
                <customPanelKind.Renderer state={customTab.state.state} ctx={hostCtx} />
              </div>
            </PluginBoundary>
          ) : hasEditorSurface ? (
            // File or doc tab: Monaco/doc editor + board split.
            <EditorBoardArea
              layout={c.editorLayout}
              onLayoutChange={c.setEditorLayout}
              persistNamespace={persistNamespace ?? "archlang.studio"}
              editor={
                <div className="flex min-h-0 min-w-0 flex-1 flex-col">
                  <div className="relative min-h-0 flex-1">{editorBody}</div>
                </div>
              }
              surface={
                <div className="relative flex min-h-0 min-w-0 flex-1 flex-col">{center}</div>
              }
            />
          ) : (
            // Board/types/process tab: full-width surface, no Monaco.
            <div className="flex min-h-0 min-w-0 flex-1">{center}</div>
          )}

          {/* Right: inspector + advisory findings */}
          {showDetails && (
            <ResizablePanel
              side="right"
              ariaLabel="details"
              widthKey={widthKey("details.width")}
              collapsed={c.sidebarCollapsed}
              onCollapsedChange={c.setSidebarCollapsed}
              showHandle={false}
              defaultWidth={300}
              {...WIDTH_TIERS.rail}
              className="overflow-y-auto border-l border-slate-800 bg-slate-900"
            >
              <DetailsPane
                root={c.root}
                boardIndexes={c.boardIndexes}
                onArchlangNav={c.handleArchlangNav}
                // Inline module editing (W1) follows the per-view edit stance,
                // same as the viewer's inspector.
                editable={editableNow}
              />
              <AdvisoryPane />
            </ResizablePanel>
          )}
        </div>
      </div>

      <SearchPalette state={c.searchState} route={c.onSearchPick} />
    </div>
  );
}
